import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send } from "lucide-react";
import Avatar from "@/components/Avatar";

const TIER_CLASS = {
  Seeker: "bg-cream-surface text-jjblue",
  Believer: "bg-gold/15 text-burgundy",
  Disciple: "bg-burgundy text-cream",
};

/**
 * Hover-card that wraps a member name/link. Shows a small floating card with
 * avatar, name, tier, bio, and a Send-message CTA after a 350ms hover delay.
 * On touch / focus, opens immediately. Click-through still works via the inner Link.
 */
export default function MemberHoverCard({ user, currentUserId, children, className = "" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const showTimer = useRef(null);
  const hideTimer = useRef(null);

  useEffect(() => () => { clearTimeout(showTimer.current); clearTimeout(hideTimer.current); }, []);

  if (!user?.id) return <span className={className}>{children}</span>;

  const isSelf = currentUserId && user.id === currentUserId;
  const tierCls = TIER_CLASS[user.tier] || TIER_CLASS.Seeker;

  const onEnter = () => {
    clearTimeout(hideTimer.current);
    showTimer.current = setTimeout(() => setOpen(true), 350);
  };
  const onLeave = () => {
    clearTimeout(showTimer.current);
    hideTimer.current = setTimeout(() => setOpen(false), 180);
  };

  return (
    <span
      ref={wrapRef}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      className={`relative inline-flex ${className}`}
    >
      {children}
      {open && (
        <div
          className="absolute z-40 top-full left-0 mt-2 w-72 jj-card !p-4 shadow-xl text-left animate-in fade-in"
          data-testid={`member-hover-${user.id}`}
          onMouseEnter={() => clearTimeout(hideTimer.current)}
          onMouseLeave={onLeave}
        >
          <div className="flex items-start gap-3">
            <Link to={`/u/${user.id}`} className="shrink-0 hover:opacity-80">
              <Avatar user={user} size={40} />
            </Link>
            <div className="flex-1 min-w-0">
              <Link to={`/u/${user.id}`} className="font-medium text-jjblue hover:text-burgundy truncate block">{user.name}</Link>
              <span className={`mt-1 inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tierCls}`}>{user.tier}</span>
            </div>
          </div>
          {user.bio && (
            <p className="mt-3 text-xs text-[var(--jj-text-secondary)] leading-relaxed line-clamp-4 whitespace-pre-wrap">{user.bio}</p>
          )}
          {!isSelf && (
            <Link
              to={`/messages/${user.id}`}
              className="mt-3 btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1.5 w-full justify-center"
              data-testid={`member-hover-message-${user.id}`}
            >
              <Send className="h-3 w-3" /> Send a message
            </Link>
          )}
        </div>
      )}
    </span>
  );
}
